import { useEffect } from 'react';

const SITE_URL = 'https://www.fysiquefactory.fit';
const SITE_NAME = 'The Fhysique Factory';
const DEFAULT_IMAGE = `${SITE_URL}/images/hero-bg.jpg`;

/**
 * Sets per-page SEO: title, meta description, canonical, OG tags, and optional JSON-LD schema.
 *
 * @param {{ title: string, description: string, path: string, schema?: object|object[] }} opts
 */
export default function useSEO({ title, description, path = '/', schema }) {
  useEffect(() => {
    const fullTitle = title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`;
    const canonicalUrl = `${SITE_URL}${path}`;

    // Title
    document.title = fullTitle;

    // Helper to set/create meta tags
    const setMeta = (attr, key, content) => {
      let el = document.querySelector(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    // Standard meta
    setMeta('name', 'description', description);

    // Open Graph
    setMeta('property', 'og:title', fullTitle);
    setMeta('property', 'og:description', description);
    setMeta('property', 'og:url', canonicalUrl);
    setMeta('property', 'og:image', DEFAULT_IMAGE);
    setMeta('property', 'og:type', 'website');
    setMeta('property', 'og:site_name', SITE_NAME);
    setMeta('property', 'og:locale', 'en_JM');

    // Twitter
    setMeta('name', 'twitter:title', fullTitle);
    setMeta('name', 'twitter:description', description);
    setMeta('name', 'twitter:image', DEFAULT_IMAGE);

    // Canonical
    let link = document.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      document.head.appendChild(link);
    }
    link.setAttribute('href', canonicalUrl);

    // JSON-LD schema(s)
    // Remove old page-level schemas
    document.querySelectorAll('script[data-page-schema]').forEach(el => el.remove());

    const schemas = schema ? (Array.isArray(schema) ? schema : [schema]) : [];
    schemas.forEach((s, i) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-page-schema', 'true');
      script.textContent = JSON.stringify(s);
      document.head.appendChild(script);
    });

    return () => {
      document.querySelectorAll('script[data-page-schema]').forEach(el => el.remove());
    };
  }, [title, description, path, schema]);
}
